import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import "./App.css";

function App() {
    // Notification bar state – change the message and variant as needed.
    const [notification, setNotification] = useState({
        message:
            "Math. just released on the App Store for iOS!",
        variant: "info" // Use "alert" for a red alert notification.
    });

    return (
        <BrowserRouter>
            <AnimatePresence>
                {notification && (
                    <NotificationBar
                        message={notification.message}
                        variant={notification.variant}
                        onClose={() => setNotification(null)}
                    />
                )}
            </AnimatePresence>
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/imprint" element={<Imprint />} />
            </Routes>
            <BackToTop />
        </BrowserRouter>
    );
}

// Notification bar component (with a close button)
const NotificationBar = ({ message, variant, onClose }) => {
    return (
        <motion.div
            className={`notification-bar ${variant}`}
            initial={{ y: -50 }}
            animate={{ y: 0 }}
            exit={{ y: -50 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
            <span>{message}</span>
            <button className="close-btn" onClick={onClose}>
                ×
            </button>
        </motion.div>
    );
};

// Home page: landing page content (Testimonials removed)
const Home = () => {
    return (
        <>
            <HeroSection />
            <FeaturesSection />
            <Footer />
        </>
    );
};

// Hero section with a subtle parallax background effect.
const HeroSection = () => {
    const [mouseX, setMouseX] = useState(0);
    const [mouseY, setMouseY] = useState(0);

    useEffect(() => {
        const handleMouseMove = (e) => {
            setMouseX(e.clientX);
            setMouseY(e.clientY);
        };
        window.addEventListener("mousemove", handleMouseMove);
        return () => window.removeEventListener("mousemove", handleMouseMove);
    }, []);

    const bgStyle = { transform: `translate(${mouseX / 100}px, ${mouseY / 100}px)` };

    return (
        <section className="hero">
            <motion.div
                className="hero-bg"
                style={bgStyle}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
            />
            <motion.h1
                className="hero-title"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
            >
                Math.
            </motion.h1>
            <motion.p
                className="hero-subtitle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
            >
                Revolutionizing calculation with intuitive design.
            </motion.p>

            <div className="download-buttons">
                {/* Enhanced iOS button with no scaling animations */}
                <motion.a
                    href="https://apps.apple.com/app/math/id6714449412" // Replace with your actual App Store link
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-ios enhanced"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.5 }}
                    style={{
                        backgroundColor: "#0070c9", // App Store blue
                        border: "none",
                        color: "#fff",
                        padding: "12px 24px",
                        borderRadius: "8px",
                        textDecoration: "none",
                        fontWeight: "bold",
                        display: "inline-block"
                    }}
                >
                    Download on iOS
                </motion.a>

                {/* macOS button – still greyed out */}
                <motion.div
                    className="btn btn-macos disabled"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.7 }}
                >
                    Download on macOS <span className="coming-soon">(Coming Soon)</span>
                </motion.div>
            </div>

            <motion.div
                className="beta-info"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1 }}
            >
                <p>Now launching in Beta / Early Access</p>
                <p>
                    We appreciate your support and welcome feedback at{" "}
                    <a href="mailto:feedback@math-app.com">feedback@math-app.com</a>
                </p>
            </motion.div>
        </section>
    );
};

// Features section.
const FeaturesSection = () => {
    const features = [
        {
            title: "Intuitive Design",
            description:
                "A modern and accessible design that redefines the calculator experience."
        },
        {
            title: "Math.AI (Coming Soon)",
            description:
                "Privacy-first, on-device AI solving complex equations fast, with optional cloud assistance."
        },
        {
            title: "iCloud Sync",
            description:
                "Sync your history, widgets, and settings seamlessly across all your devices."
        },
        {
            title: "Widgets & Shortcuts",
            description:
                "Access your calculations instantly with handy widgets and shortcuts."
        },
        {
            title: "Versatile Tools",
            description:
                "Includes currency conversion, tip calculator with bill splitting, unit conversion, and more."
        }
    ];

    return (
        <section className="features">
            <h2>Features</h2>
            <div className="features-grid">
                {features.map((feature, index) => (
                    <motion.div
                        key={index}
                        className="feature-card glass"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{
                            scale: 1.02,
                            transition: { type: "spring", stiffness: 200, damping: 15 }
                        }}
                        transition={{ duration: 0.6, delay: index * 0.2 }}
                    >
                        <h3>{feature.title}</h3>
                        <p>{feature.description}</p>
                    </motion.div>
                ))}
            </div>
        </section>
    );
};

// Footer with legal links.
const Footer = () => {
    return (
        <footer className="footer">
            <div className="footer-links">
                <Link to="/privacy-policy">Privacy Policy</Link>
                <Link to="/terms-of-service">Terms of Service</Link>
                <Link to="/imprint">Imprint</Link>
            </div>
            <p>&copy; {new Date().getFullYear()} Horton AppWorks. All rights reserved.</p>
        </footer>
    );
};

// Back-to-top button.
const BackToTop = () => {
    const [visible, setVisible] = useState(false);

    const toggleVisibility = () => {
        if (window.pageYOffset > 300) {
            setVisible(true);
        } else {
            setVisible(false);
        }
    };

    useEffect(() => {
        window.addEventListener("scroll", toggleVisibility);
        return () => window.removeEventListener("scroll", toggleVisibility);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <motion.div
            className="back-to-top"
            onClick={scrollToTop}
            initial={{ opacity: 0 }}
            animate={{ opacity: visible ? 1 : 0 }}
            whileHover={{
                scale: 1.1,
                transition: { type: "spring", stiffness: 300, damping: 20 }
            }}
            transition={{ duration: 0.3 }}
        >
            &#8679;
        </motion.div>
    );
};

// ----- Legal Pages with Full Legal Text ----- //

const PrivacyPolicy = () => {
    return (
        <div className="legal-page">
            <h1>Privacy Policy</h1>
            <p>
                <strong>Last updated:</strong> January 1, 2025
            </p>
            <p>
                This Privacy Policy explains how Horton AppWorks ("we", "us", or "our")
                collects, uses, discloses, and safeguards your information when you use
                our application “Math.”. We are committed to protecting your personal
                data and your right to privacy.
            </p>
            <h2>1. Information We Collect</h2>
            <p>
                We may collect information that you voluntarily provide to us when you use
                our app. This includes your name, email address, and any other
                information you choose to share. In addition, we collect technical data
                such as device type, operating system, and usage data to help us improve
                our service.
            </p>
            <h2>2. How We Use Your Information</h2>
            <p>We use the collected information to:</p>
            <ul>
                <li>Provide and maintain our service</li>
                <li>Improve and personalize your experience</li>
                <li>Understand and analyze how you use our service</li>
                <li>
                    Communicate with you, including sending service-related announcements
                </li>
            </ul>
            <h2>3. Sharing Your Information</h2>
            <p>
                We do not sell your personal information. We may share your information
                with trusted third-party service providers who assist us in operating our
                service, subject to confidentiality agreements and only for the purposes
                described in this Privacy Policy.
            </p>
            <h2>4. Data Security</h2>
            <p>
                We implement appropriate technical and organizational measures to
                protect your personal data. While we strive to protect your information,
                no method of transmission over the Internet or electronic storage is
                100% secure.
            </p>
            <h2>5. Your Rights</h2>
            <p>
                You have the right to access, correct, or delete your personal data. You
                may also object to or restrict certain processing of your data. To
                exercise these rights, please contact us at{" "}
                <a href="mailto:privacy@math-app.com">privacy@math-app.com</a>.
            </p>
            <h2>6. Cookies and Tracking Technologies</h2>
            <p>
                We use cookies and similar tracking technologies to enhance your
                experience and analyze how our service is used. You can control the use
                of cookies through your browser settings.
            </p>
            <h2>7. Changes to This Privacy Policy</h2>
            <p>
                We may update this Privacy Policy from time to time. We will notify you of
                any changes by posting the new Privacy Policy on this page with an
                updated “Last updated” date.
            </p>
            <h2>8. Contact Us</h2>
            <p>
                If you have any questions or concerns about this Privacy Policy, please
                contact us at <a href="mailto:privacy@math-app.com">privacy@math-app.com</a>.
            </p>
            <Link to="/">Back to Home</Link>
        </div>
    );
};

const TermsOfService = () => {
    return (
        <div className="legal-page">
            <h1>Terms of Service</h1>
            <p>
                <strong>Last updated:</strong> January 1, 2025
            </p>
            <p>
                These Terms of Service ("Terms") govern your access to and use of the
                Math. application provided by Horton AppWorks ("we", "us", or "our").
                By using our service, you agree to be bound by these Terms.
            </p>
            <h2>1. Acceptance of Terms</h2>
            <p>
                By accessing or using the Math. app, you agree to these Terms. If you do
                not agree to these Terms, please do not use our service.
            </p>
            <h2>2. Description of Service</h2>
            <p>
                Math. is an innovative calculator application designed to provide a
                modern, intuitive, and accessible calculation experience. The service is
                currently in Beta/Early Access.
            </p>
            <h2>3. User Responsibilities</h2>
            <p>
                You agree to use the service in accordance with all applicable laws and
                not to misuse or interfere with the service. You are responsible for
                maintaining the confidentiality of any account credentials.
            </p>
            <h2>4. Intellectual Property</h2>
            <p>
                All content, trademarks, and data provided on the Math. app are the
                property of Horton AppWorks or its licensors. You agree not to reproduce,
                duplicate, copy, sell, or exploit any portion of the service without our
                express written consent.
            </p>
            <h2>5. Limitation of Liability</h2>
            <p>
                In no event shall Horton AppWorks be liable for any direct, indirect,
                incidental, special, or consequential damages arising out of or in
                connection with your use of the service.
            </p>
            <h2>6. Termination</h2>
            <p>
                We reserve the right to terminate or suspend your access to the service at
                any time, without notice, for conduct that we believe violates these
                Terms or is harmful to other users or our business.
            </p>
            <h2>7. Governing Law</h2>
            <p>
                These Terms shall be governed by and construed in accordance with the laws
                of the applicable jurisdiction without regard to its conflict of laws
                provisions.
            </p>
            <h2>8. Changes to These Terms</h2>
            <p>
                We may modify these Terms at any time. Any changes will be posted on this
                page with an updated effective date. Your continued use of the service
                after such changes constitutes your acceptance of the new Terms.
            </p>
            <h2>9. Contact Information</h2>
            <p>
                For any questions about these Terms, please contact us at{" "}
                <a href="mailto:tos@math-app.com">tos@math-app.com</a>.
            </p>
            <Link to="/">Back to Home</Link>
        </div>
    );
};

const Imprint = () => {
    return (
        <div className="legal-page">
            <h1>Imprint</h1>
            <p>
                <strong>Horton AppWorks</strong>
            </p>
            <p>
                <strong>Address:</strong> Untere Dorfstr. 103, 57074, Siegen
            </p>
            <p>
                <strong>Email:</strong>{" "}
                <a href="mailto:contact@math-app.com">contact@math-app.com</a>
            </p>
            <p>
                <strong>Managing Director:</strong> Niklas Michel
            </p>
            <p>
                This imprint is published in accordance with applicable legal requirements
                and provides information about the entity responsible for this website.
            </p>
            <Link to="/">Back to Home</Link>
        </div>
    );
};

export default App;